import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../ui/dialog";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { toast } from "sonner";
import { userService } from "../../../services/user.service";

const ESTADO_CIVIL_OPTIONS = ["Solteiro(a)", "Casado(a)", "Divorciado(a)", "Viúvo(a)"];
const TIPO_CONTA_OPTIONS = ["corrente", "poupanca"];
const TIPO_PIX_OPTIONS = ["cpf", "email", "telefone", "aleatorio"];

function Field({ label, children, required = false, optional = false }) {
  return (
    <div className="space-y-1">
      <Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
        {label}
        {required && <span className="text-red-500 ml-0.5">*</span>}
        {optional && <span className="text-muted-foreground font-normal normal-case ml-1 text-xs">(opcional)</span>}
      </Label>
      {children}
    </div>
  );
}

export function CedenteEditDialog({ isOpen, onOpenChange, cedente, onSuccess }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!cedente?.id) return;
    setLoading(true);
    userService.getUserById(cedente.id)
      .then(full => {
        setForm({
          cpf: full.cpf || "",
          nome: full.nome || "",
          rg: full.rg || "",
          email: full.email || "",
          telefone: full.telefone || "",
          data_nascimento: full.data_nascimento || "",
          estado_civil: full.estado_civil || "",
          nome_conjuge: full.nome_conjuge || "",
          cpf_conjuge: full.cpf_conjuge || "",
          banco: full.banco || "",
          agencia: full.agencia || "",
          conta_corrente: full.conta_corrente || "",
          digito_conta: full.digito_conta || "",
          tipo_conta: full.tipo_conta || "",
          pix: full.pix || "",
          tipo_pix: full.tipo_pix || "",
          endereco_logradouro: full.endereco_logradouro || "",
          endereco_numero: full.endereco_numero || "",
          endereco_complemento: full.endereco_complemento || "",
          endereco_bairro: full.endereco_bairro || "",
          endereco_cidade: full.endereco_cidade || "",
          endereco_estado: full.endereco_estado || "",
          endereco_cep: full.endereco_cep || "",
        });
      })
      .catch(() => toast.error("Erro ao carregar dados do substituído"))
      .finally(() => setLoading(false));
  }, [cedente?.id]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSave = async () => {
    if (!cedente?.id) return;
    setSaving(true);
    try {
      const payload = { ...form };
      Object.keys(payload).forEach(key => {
        if (payload[key] === "") payload[key] = null;
      });

      await userService.updateCedente(cedente.id, payload);
      toast.success("Dados do substituído atualizados");
      onSuccess?.();
      onOpenChange(false);
    } catch (e) {
      let errorMsg = e.message;
      if (e.response?.data?.detail) {
        if (Array.isArray(e.response.data.detail)) {
          errorMsg = e.response.data.detail.map(d => `${d.loc[d.loc.length - 1]}: ${d.msg}`).join(", ");
        } else {
          errorMsg = e.response.data.detail;
        }
      }
      toast.error("Erro ao salvar: " + errorMsg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Editar Dados do Substituído</DialogTitle>
        </DialogHeader>

        {loading && (
          <div className="py-8 text-center text-sm text-muted-foreground">Carregando dados...</div>
        )}
        <div className="space-y-6 py-2" style={{ display: loading ? 'none' : undefined }}>
          {/* Dados Pessoais */}
          <section>
            <h3 className="text-sm font-bold text-foreground mb-3 border-b pb-1">Dados Pessoais</h3>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Nome completo">
                <Input value={form.nome || ""} onChange={set("nome")} />
              </Field>
              <Field label="CPF">
                <Input value={form.cpf || ""} onChange={set("cpf")} />
              </Field>
              <Field label="RG" required>
                <Input value={form.rg || ""} onChange={set("rg")} />
              </Field>
              <Field label="Data de Nascimento" required>
                <Input type="date" value={form.data_nascimento || ""} onChange={set("data_nascimento")} />
              </Field>
              <Field label="E-mail" required>
                <Input value={form.email || ""} onChange={set("email")} />
              </Field>
              <Field label="Telefone" required>
                <Input value={form.telefone || ""} onChange={set("telefone")} />
              </Field>
              <Field label="Estado Civil" required>
                <select
                  className="w-full h-9 rounded-md border border-input bg-background px-3 py-1 text-sm"
                  value={form.estado_civil || ""}
                  onChange={set("estado_civil")}
                >
                  <option value="">Selecione...</option>
                  {ESTADO_CIVIL_OPTIONS.map((o) => (
                    <option key={o} value={o}>{o}</option>
                  ))}
                </select>
              </Field>
            </div>
            {(form.estado_civil === "Casado(a)") && (
              <div className="grid grid-cols-2 gap-3 mt-3">
                <Field label="Nome do Cônjuge">
                  <Input value={form.nome_conjuge || ""} onChange={set("nome_conjuge")} />
                </Field>
                <Field label="CPF do Cônjuge" optional>
                  <Input value={form.cpf_conjuge || ""} onChange={set("cpf_conjuge")} />
                </Field>
              </div>
            )}
          </section>

          {/* Endereço */}
          <section>
            <h3 className="text-sm font-bold text-foreground mb-3 border-b pb-1">Endereço</h3>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Field label="Logradouro" required>
                  <Input value={form.endereco_logradouro || ""} onChange={set("endereco_logradouro")} />
                </Field>
              </div>
              <Field label="Número" required>
                <Input value={form.endereco_numero || ""} onChange={set("endereco_numero")} />
              </Field>
              <Field label="Complemento" optional>
                <Input value={form.endereco_complemento || ""} onChange={set("endereco_complemento")} />
              </Field>
              <Field label="Bairro" required>
                <Input value={form.endereco_bairro || ""} onChange={set("endereco_bairro")} />
              </Field>
              <Field label="Cidade" required>
                <Input value={form.endereco_cidade || ""} onChange={set("endereco_cidade")} />
              </Field>
              <Field label="Estado (UF)" required>
                <Input value={form.endereco_estado || ""} onChange={set("endereco_estado")} maxLength={2} />
              </Field>
              <Field label="CEP" required>
                <Input value={form.endereco_cep || ""} onChange={set("endereco_cep")} />
              </Field>
            </div>
          </section>

          {/* Dados Bancários */}
          <section>
            <h3 className="text-sm font-bold text-foreground mb-3 border-b pb-1">Dados Bancários</h3>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Banco">
                <Input value={form.banco || ""} onChange={set("banco")} />
              </Field>
              <Field label="Agência" required>
                <Input value={form.agencia || ""} onChange={set("agencia")} />
              </Field>
              <Field label="Conta" required>
                <Input value={form.conta_corrente || ""} onChange={set("conta_corrente")} />
              </Field>
              <Field label="Dígito">
                <Input value={form.digito_conta || ""} onChange={set("digito_conta")} maxLength={2} />
              </Field>
              <Field label="Tipo de Conta" required>
                <select
                  className="w-full h-9 rounded-md border border-input bg-background px-3 py-1 text-sm"
                  value={form.tipo_conta || ""}
                  onChange={set("tipo_conta")}
                >
                  <option value="">Selecione...</option>
                  {TIPO_CONTA_OPTIONS.map((o) => (
                    <option key={o} value={o}>{o === "corrente" ? "Corrente" : "Poupança"}</option>
                  ))}
                </select>
              </Field>
              <Field label="Chave PIX" required>
                <Input value={form.pix || ""} onChange={set("pix")} />
              </Field>
              <Field label="Tipo de PIX">
                <select
                  className="w-full h-9 rounded-md border border-input bg-background px-3 py-1 text-sm"
                  value={form.tipo_pix || ""}
                  onChange={set("tipo_pix")}
                >
                  <option value="">Selecione...</option>
                  {TIPO_PIX_OPTIONS.map((o) => (
                    <option key={o} value={o}>{o}</option>
                  ))}
                </select>
              </Field>
            </div>
          </section>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancelar
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? "Salvando..." : "Salvar Alterações"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
